import {Injectable} from '@angular/core';
import {WizardNestedFormService} from '../abstract/wizard-nested-form-service';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {minLengthValidator} from '@helpers/validators/minLength';
import {SpecialistsProvider} from '@providers/specialists/specialists.provider';
import {map, takeUntil} from 'rxjs/operators';
import {WizardFormEventsService} from '../wizard-form-events.service';
import {EventsService} from '@services/events.service';

@Injectable()
export class LaboratoryStaffService extends WizardNestedFormService {
    form = this.fb.group({
        _touched: [false, Validators.requiredTrue],

        /**
         * Список сотрудников, заявленых на аттестацию. Хранятся только Id сотрудников
         * коллекция FormGroup вида [{id: 2}, {id: 55}, ...]
         */
        ids_list: this.fb.control([], minLengthValidator(1)),

        /**
         * Покрывают ли выбранные в list сотрудники заявленную область аттестации
         */
        isCovered: [false, Validators.requiredTrue],
    });

    constructor(
        private fb: FormBuilder,
        private specialistsProvider: SpecialistsProvider,
        private wizardFormEventsService: WizardFormEventsService,
        private eventsService: EventsService,
    ) {
        super();
    }

    init() {
        this.specialistsProvider.notFired$.pipe(
            takeUntil(this.destroyed$),
            map(list => list.map(s => +s.id))
        ).subscribe((ids: number[]) => {
            /**
             * Если с частью формы (шагом) уже работали, то просто удаляем уволенных цаецов из формы.
             * Если еще не работали, то по-умолчанию добавляем всех возможных специалистов
             */
            if (this.isFormTouched()) {
                this.removeUnexistedIds(ids);
            } else {
                this.setIdsToList(ids);
            }
        });

        this.listIdsControl.valueChanges.pipe(
            takeUntil(this.destroyed$),
        ).subscribe((ids) => {
            this.wizardFormEventsService.setStaffIds(ids);
        });

        this.listIdsControl.updateValueAndValidity();
    }

    restoreValidators() {

    }

    /**
     * Добавить специалиста
     * @param id
     */
    add(id: number): void {
        if (this.currentValue.includes(id)) {
            this.eventsService.notyShow$.next({
                type: 'warning',
                content: 'Специалист уже выбран ранее'
            });
            return;
        }

        this.listIdsControl.patchValue(this.currentValue.concat([id]));
    }

    /**
     * Удалить специалистов
     * @param ids
     */
    remove(ids: number[]): void {
        if (!ids || !ids.length) {
            return;
        }
        const newValue = this.currentValue.filter(id => !ids.includes(id));
        this.listIdsControl.patchValue(newValue);
    }

    /**
     * Добавить все переданные ids в форму
     * @param ids
     */
    private setIdsToList(ids: number[]) {
        this.listIdsControl.patchValue(ids);
    }

    /**
     * Удалить из формы id всех несуществующих специалистов, зная, какие специалисты сейчас существуют в организации и какие выбраны в форме
     * @param existedIds
     */
    private removeUnexistedIds(existedIds: number[]) {
        const currentExisted = this.currentValue.filter(id => existedIds.includes(id));

        if (currentExisted.length !== this.currentValue.length) {
            this.listIdsControl.patchValue(currentExisted);
        }
    }

    /**
     * Текущее значение специалистов
     */
    private get currentValue(): number[] {
        return (this.listIdsControl.value && this.listIdsControl.value instanceof Array ? this.listIdsControl.value : []).map(id => +id);
    }

    private get listIdsControl(): FormControl {
        return this.form.get('ids_list') as FormControl;
    }
}
